/** SSE JSON handlers keyed by normalized `type` (see normalizeSseEventType). */
import { useDevAgentStore } from "@/stores/devAgentStore";
import { updateSessionState } from "../devAgentService";
import { sseState } from "./sseMutableState";
import { CODE_TYPES, STEP_LABELS } from "./sseConstants";
import { S, add, upsertThinking, clearThinking, updatePlanStep } from "./sseFeedHelpers";

/** True if payload indicates Salesforce token is invalid / unauthorized (SSE or nested error). */
export function markSfDisconnectedIfSalesforceAuth(d: unknown): boolean {
  if (d == null || typeof d !== "object") return false;
  const o = d as Record<string, unknown>;
  const data = o.data && typeof o.data === "object" ? (o.data as Record<string, unknown>) : null;
  const code = typeof o.code === "string" ? o.code : data && typeof data.code === "string" ? data.code : "";
  const typ = typeof o.type === "string" ? o.type : "";
  const err = typeof o.error === "string" ? o.error : "";
  const msg = typeof o.message === "string" ? o.message : "";
  const hay = `${code}\n${typ}\n${err}\n${msg}`.toLowerCase();
  const hit =
    code === "sf_token_unauthorized" ||
    typ === "sf_token_unauthorized" ||
    hay.includes("sf_token_unauthorized") ||
    hay.includes("salesforce_token_unauthorized") ||
    /sf[_\s]?(token|auth)[^\n]*unauthor/i.test(hay);
  if (hit) useDevAgentStore.getState().setSfConnected(false);
  return hit;
}

export const sseEventHandlers: Record<string, (d: any) => void> = {
  replace_streaming_content: (d: any) => {
    const content = d?.content ?? d?.data?.content ?? "";
    useDevAgentStore.setState({ streamingContent: String(content || "") });
  },
  retry_started: (d: any) => {
    // Backend evaluator failed the last answer and is retrying (promoted route
    // or same route). Clear the streamed content the user saw and show a
    // status so they know something's happening instead of watching text
    // vanish. The fresh answer streams in via normal text_delta events.
    useDevAgentStore.setState({ streamingContent: "" });
    upsertThinking(d?.message || "Finding a better answer...", "retry_started");
  },
  run_start: (d: any) => {
    const store = S();
    // Validate SSE belongs to this session
    if (d?.session_uuid && store.sessionUuid && d.session_uuid !== store.sessionUuid) {
      console.warn("[SSE] run_start session mismatch — ignoring");
      return;
    }
    // Capture org/user from backend for downstream validation
    sseState.runOrgId = d?.org_id || "";
    sseState.runUserId = d?.user_id || "";
    store.setRunStatus("running"); store.clearPipelineSteps();
    store.setDismissedPanels(new Set());
    sseState.activeThinkingId = null; sseState.finalized = false;
    sseState.saveResultSeen = false; sseState.toolCallMap.clear();
    for (const item of store.feedItems) {
      if (item.type === "thinking") store.removeFeedItem(item.id);
      if (item.type === "tool_call" && item.data.status === "running") store.updateFeedItem(item.id, { status: "done" });
    }
    armSilenceTimer();
  },
  run_end: () => {
    // Reset diagnostic counters for next run
    sseState.eventsReceived = 0;
    sseState.gapsDetected = 0;
    sseState.sessionStart = Date.now();

    if (sseState.stuckTimer) { clearTimeout(sseState.stuckTimer); sseState.stuckTimer = null; }
    S().setRunStatus("idle"); clearThinking();
    const sc = S().streamingContent;
    if (sc) {
      if (!sseState.finalized) sseState.finalized = true;
      const pending = sseState.pendingPersisted;
      sseState.pendingPersisted = null;
      S().finalizeStreaming(sc, pending || undefined);
    }
    sseState.toolCallMap.forEach((feedId) => S().updateFeedItem(feedId, { status: "done" }));
    sseState.toolCallMap.clear();
    // Step-by-step plan: mark any running step as completed on run_end
    const planItem = S().feedItems.find((f) => f.type === "plan" && f.data.isApproved);
    if (planItem) {
      const steps = [...(planItem.data.steps || [])];
      let changed = false;
      steps.forEach((s: any, i: number) => { if (s.status === "running") { steps[i] = { ...s, status: "completed" }; changed = true; } });
      if (changed) S().updateFeedItem(planItem.id, { steps });
    }
    const uuid = S().sessionUuid;
    if (uuid) {
      const bullets = S().conversationSummary;
      updateSessionState(uuid, {
        session_cost: S().sessionCost,
        session_summary: bullets.length > 0 ? { bullets } : undefined,
      }).catch(() => {});
      import("../devAgentService").then(({ getSessionState }) => {
        getSessionState(uuid).then((state) => {
          if (S().sessionUuid !== uuid) return;
          if (state.dependencies) S().setDependencies(state.dependencies as any);
        }).catch(() => {});
      });
      recoverFromDB();
    }
  },

  /** Backend emits this right before `run_end` so the frontend can bind
   *  the message_uuid + tool_calls to the chat_agent feed item that
   *  run_end is about to create from the streaming buffer. We stash the
   *  correlation here and run_end applies it when it calls finalizeStreaming.
   *
   *  We also walk already-materialised feed items as a fallback — the
   *  hydrate path creates them directly without going through streaming,
   *  and older flows may already have a feed item by the time this fires. */
  message_persisted: (d: any) => {
    const data = d?.data || d;
    const msgUuid = data?.message_uuid;
    const tcs = data?.tool_calls || [];
    if (!msgUuid) return;

    // Fallback path: if a chat_agent item already exists without a
    // messageUuid, patch it retroactively. Covers the non-streaming case.
    const items = S().feedItems;
    for (let i = items.length - 1; i >= 0; i--) {
      const it = items[i];
      if (it.type === "chat_agent" && !it.data?.messageUuid) {
        S().updateFeedItem(it.id, { messageUuid: msgUuid, toolCalls: tcs });
        return;
      }
    }

    // Streaming path: stash so run_end → finalizeStreaming picks it up.
    sseState.pendingPersisted = { messageUuid: msgUuid, toolCalls: tcs };
  },
  context_compressed: (d: any) => {
    const data = d?.data || d;
    const before = Number(data?.before_tokens || 0);
    const after = Number(data?.after_tokens || 0);
    const freed = Number(data?.tokens_freed || 0);
    if (freed <= 0) return;
    add("chat_agent", {
      content:
        `🗜️ **Context compressed** — freed ${freed.toLocaleString()} tokens ` +
        `(${before.toLocaleString()} → ${after.toLocaleString()}).`,
    });
  },
  error: (d) => {
    markSfDisconnectedIfSalesforceAuth(d);
    S().setRunStatus("failed");
    clearThinking();
    if (d.error) add("error", { message: d.error });
  },

  /** Dedicated backend event when SF OAuth token is rejected. */
  sf_token_unauthorized: () => {
    useDevAgentStore.getState().setSfConnected(false);
  },

  // Real-time dep snapshot pushed by prefetch_context_node right after
  // persisting session.metadata.dependencies. Appends to the snapshot list
  // (or replaces an existing entry for the same target) — the panel grows
  // as the user runs more refactor queries.
  dependencies_ready: (d) => {
    const payload = d?.data || d;
    if (!payload || typeof payload !== "object") return;
    const { target, callers, tests } = payload;
    if (!target && !Array.isArray(callers) && !Array.isArray(tests)) return;
    S().addDependency({
      target: target || "",
      target_id: payload.target_id ?? payload.targetId ?? null,
      callers: Array.isArray(callers) ? callers : [],
      tests: Array.isArray(tests) ? tests : [],
      status: payload.status || "awaiting_user_scope",
      total: payload.total ?? ((callers?.length || 0) + (tests?.length || 0)),
      captured_at: payload.captured_at || new Date().toISOString(),
    });
  },

  thinking_step: (d) => {
    const step = d.step; if (!step?.id) return;
    if (step.name === "save_result" || step.id === "save_result") { sseState.saveResultSeen = true; clearThinking(); return; }
    if (sseState.saveResultSeen) return;
    const friendly = STEP_LABELS[step.name || step.id]; if (!friendly) return;
    if ((step.status === "started" ? "running" : step.status) === "running") { upsertThinking(friendly, step.id); S().addPipelineStep(friendly); }
  },
  agent_activity: (d) => {
    const data = d.data || d;
    const friendly = STEP_LABELS[data.agent_code] || data.agent_name; if (!friendly) return;
    if (data.status === "started" || data.status === "running") upsertThinking(friendly, data.agent_code);
    else if (data.status === "completed") clearThinking();
  },
  pipeline_status: (d) => {
    if (sseState.saveResultSeen) return;
    const data = d.data || d;
    const friendly = STEP_LABELS[data.step] || data.detail; if (!friendly) return;
    if (data.step === "complete") clearThinking();
    else { upsertThinking(friendly, data.step); S().addPipelineStep(friendly); }
  },

  tool_call_start: (d) => {
    const t = d.tool_call || d.tool || {}; clearThinking();
    S().addFeedItem({ type: "tool_call" as const, data: { name: t.name || "tool", args: t.arguments, status: "running", startedAt: Date.now() } });
    const last = S().feedItems[S().feedItems.length - 1];
    if (last) sseState.toolCallMap.set(t.id || t.name, last.id);
  },
  tool_call_end: (d) => {
    const r = d.tool_call || d.result || {};
    if (r.error) markSfDisconnectedIfSalesforceAuth(r);
    const key = r.id || r.name || d.name;
    const feedId = sseState.toolCallMap.get(key);
    if (feedId) {
      const started = S().feedItems.find((f) => f.id === feedId)?.data?.startedAt || Date.now();
      S().updateFeedItem(feedId, { status: r.error ? "error" : "done", output: r.result || r.output, error: r.error, durationMs: Date.now() - started });
      sseState.toolCallMap.delete(key);
    } else {
      add("tool_call", { name: r.name || key || "tool", args: {}, status: r.error ? "error" : "done", output: r.result || r.output, error: r.error });
    }
  },

  task_plan: (d) => {
    const plan = d.plan; if (!plan) return;
    // Dedup: if a plan already exists, update it instead of adding a new one
    const existing = S().feedItems.find((f) => f.type === "plan");
    if (existing) {
      const steps = (plan.subtasks || plan.sections?.[0]?.subtasks || []).map((s: any, i: number) => ({ step_number: s.step_number || i + 1, description: s.description || s.label || `Step ${i + 1}`, status: s.status || "pending" }));
      S().updateFeedItem(existing.id, { steps, summary: plan.summary || existing.data.summary, total: steps.length });
      return;
    }
    for (const sec of plan.sections || [{ id: "plan-1", title: plan.summary || "Plan", subtasks: plan.subtasks }]) {
      const steps = (sec.subtasks || []).map((s: any, i: number) => ({ step_number: s.step_number || i + 1, description: s.description || s.label || `Step ${i + 1}`, status: s.status || "pending" }));
      add("plan", { steps, summary: sec.title || plan.summary || "Analysis", total: steps.length });
    }
  },
  code_change: (d) => {
    const changesetId = d.changeset_id || "";
    const isAtomic = d.atomic === true;
    const changes = d.changes || [];
    for (const ch of changes) {
      const fileName = ch.file_name || "unknown";
      const existing = S().feedItems.find((f) => f.type === "code_change" && f.data.fileName === fileName);
      if (existing) {
        S().updateFeedItem(existing.id, { code: ch.code || ch.modified || existing.data.code, diff: ch.diff || ch.code || existing.data.diff, original: ch.original || existing.data.original, version: (existing.data.version || 1) + 1, changesetId, atomic: isAtomic });
      } else {
        add("code_change", { fileName, language: ch.language || "text", action: ch.action || "create", code: ch.code || ch.modified || "", diff: ch.diff || ch.code || "", original: ch.original || "", sectionId: ch.section_id, version: ch.version || 1, metadataType: ch.metadata_type || "ApexClass", metadataId: ch.metadata_id || "", changesetId, atomic: isAtomic });
      }
    }
    // Don't auto-open editor — code shows in chat first via "Add to Workspace" buttons
    // User clicks "Add to Workspace" or "Compare" to open in editor
  },
  approval_required: (d) => {
    const approvalId = d.approval_id || "";
    // Dedup: skip if approval with same ID already exists
    if (approvalId && S().feedItems.find((f) => f.type === "approval" && f.data.approvalId === approvalId)) return;
    const approval = { approvalId, action: d.action || "unknown", description: d.message || `Approve ${d.action}?`, changesPreview: (d.changes_preview || []).map((c: any) => ({ file: c.file, type: c.type, action: c.action })), codeSnippets: d.code_snippets || [] };
    S().setPendingApproval(approval); add("approval", approval);
  },
  validation_result: (d) => {
    for (const ch of d.file_results || []) {
      const item = S().feedItems.find((f) => f.type === "code_change" && f.data.fileName === ch.file_name);
      if (item) S().updateFeedItem(item.id, { validationStatus: ch.passed ? "passed" : "failed", validationIssues: ch.issues || [] });
    }
  },
  suggested_actions: (d) => { const actions = d.data?.actions || d.actions || []; if (actions.length > 0) S().setSuggestedActions(actions); },

  transparency_report: (d) => {
    const report = d.data || d, cost = report.cost || {}, timing = report.timing || {};
    const rawCost = cost.total_cost || report.total_cost_usd || "0";
    const totalCost = typeof rawCost === "string" ? parseFloat(rawCost.replace("$", "")) || 0 : rawCost;
    S().accumulateTransparencyReport({
      total_cost_usd: totalCost, total_duration_ms: timing.total_ms || report.total_duration_ms || 0,
      llm_calls: report.llm_calls || 0, tool_calls_count: cost.tool_calls || report.tool_calls_count || 0,
      reasoning_steps: report.reasoning || report.reasoning_steps || [],
      org_tier: report.org_tier, model_used: report.model_used,
      tokens_used: cost.llm_tokens || report.tokens_used || 0,
      input_tokens: cost.input_tokens || report.input_tokens || 0,
      output_tokens: cost.output_tokens || report.output_tokens || 0,
      cache_hit_rate: cost.cache_hits || report.cache_hit_rate || 0,
    });
  },
  query_rewrite: (d) => {
    const data = d.data || d;
    if (data.duplicate && data.kind === "exact") return;
    if (data.rewritten) add("thinking", { label: `Interpreted as: "${data.rewritten}"`, stepId: "query_rewrite", isRewrite: true, original: data.original, reason: data.reason });
    if (data.duplicate && data.kind === "fuzzy") add("thinking", { label: data.message || "Similar to recent question", stepId: "dup_guard" });
  },
  tool_degraded: (d) => {
    const data = d.data || d;
    add("thinking", { label: data.message || `${data.consecutive_failures} tools failed`, stepId: "tool_degraded", isDegraded: true, severity: data.severity || "warning" });
  },
  checkpoint_request: (d) => {
    const data = d.data || d;
    const existing = S().feedItems.find((f) => f.type === "checkpoint");
    const payload = { round: data.round || data.completed_wave || 0, tools_called: data.tools_called || data.completed_count || 0, tools_failed: data.tools_failed || data.failed_count || 0, tool_names: data.tool_names || data.next_steps || [], summary: data.summary || "", message: data.message || "Progress checkpoint" };
    if (existing) S().updateFeedItem(existing.id, payload); else add("checkpoint", payload);
  },
  sub_agent_start: (d) => {
    const step = d.step || 0, avatar = d.avatar || "agent";
    add("thinking", { label: `${avatar} — ${d.description || `Step ${step}`}`, stepId: `sub_agent_${step}`, isSubAgent: true, avatar, step, wave: d.wave, status: "running" });
  },
  sub_agent_end: (d) => {
    const step = d.step || 0, status = d.status || "completed";
    const item = S().feedItems.find((f) => f.type === "thinking" && f.data.stepId === `sub_agent_${step}`);
    if (item) {
      S().updateFeedItem(item.id, { status, label: `${d.avatar || "agent"} — ${status}` });
      setTimeout(() => { S().removeFeedItem(item.id); }, status === "completed" ? 2000 : 5000);
    }
  },
  plan_generated: (d) => {
    if (S().feedItems.find((f) => f.type === "plan")) return;
    const plan = d.plan || {};
    const steps = (plan.steps || []).map((s: any, i: number) => ({ step_number: s.step_number || i + 1, description: s.description || `Step ${i + 1}`, status: "pending" }));
    add("plan", { steps, summary: plan.summary || "", total: steps.length });
  },
  plan_step_start: (d) => updatePlanStep(d.step, "running"),
  plan_step_complete: (d) => updatePlanStep(d.step, "completed"),
  plan_step_failed: (d) => updatePlanStep(d.step, "failed"),
  clarification_request: (d) => {
    const msg = d.message || "Could you clarify?";
    // Only one active clarification at a time — dedup by existence, not message
    if (S().feedItems.find((f) => f.type === "clarification")) return;
    // Discard any streaming content accumulated before clarification — it's not a real response
    useDevAgentStore.setState({ streamingContent: null });
    sseState.finalized = true; // prevent run_end from finalizing stale content
    clearThinking();
    add("clarification", { message: msg, suggestions: d.suggestions || [], option_type: d.option_type || "file", search_enabled: d.search_enabled ?? true, input_enabled: d.input_enabled ?? true, original_query: d.original_query || "" });
    S().setRunStatus("idle");
  },
  governor_report: (d) => { S().setGovernorReport(d.data || d); },

  artifact_loaded: (d) => {
    const name = d.name || ""; if (!name) return;
    const metadataId = d.metadataId || d.metadata_id || "";
    const metadataType = d.metadataType || d.metadata_type || "ApexClass";
    const content = d.content || "";
    if (!metadataId || metadataId === "unknown") return;
    if (!CODE_TYPES.has(metadataType)) return;
    if (!content || content.length < 10 || content.includes("(hidden)") || content.includes("Managed package")) return;
    // Skip name check if this is a generate/refactor query — the class name
    // may not appear in the user's message (e.g. "generate test for most recently modified class")
    const isGenerateQuery = d.intent === "generate" || d.action === "create";
    if (!isGenerateQuery) {
      const lastUserMsg = [...S().feedItems].reverse().find((f) => f.type === "chat_user");
      const userText = (lastUserMsg?.data?.content || "").toLowerCase();
      const nameLower = name.replace(/\.(cls|trigger)$/i, "").toLowerCase();
      if (!userText.includes(nameLower)) return;
    }
    const cleanContent = content.replace(/^\[.*?\].*?\nLines:\s*\d+\n\n/, "");
    if (sseState.openedFiles.has(name)) return;
    sseState.openedFiles.add(name);
    S().setEditorOpen(true);
    S().setPendingFile({ id: metadataId, name, metadataType, content: cleanContent });
  },
  summary: (d) => {
    const summary = d.summary || d, sections = summary.sections || [], bullets: string[] = [];
    if (summary.title && typeof summary.title === "string") bullets.push(summary.title);
    for (const sec of sections) {
      if (sec.title && sec.title !== summary.title) bullets.push(sec.title);
      for (const f of sec.findings || []) { if (f.description) bullets.push(`${f.name || ""}: ${f.description}`.trim()); }
      for (const sub of sec.subtasks || []) { if (sub.description) bullets.push(sub.description); }
    }
    if (bullets.length > 0) {
      const existing = S().conversationSummary || [];
      const unique = bullets.filter((b) => !existing.includes(b));
      if (unique.length > 0) S().setConversationSummary([...existing, ...unique]);
    }
  },
  // Backend keepalive — fired by _heartbeat() in app/agent/loop.py every ~5s
  // regardless of whether anything else is happening. We don't render
  // anything for it; it exists ONLY to prove the backend is alive so the
  // silence gate (armSilenceTimer below) doesn't fire prematurely. The
  // useDevAgentSSE.ts event loop calls armSilenceTimer() on EVERY incoming
  // event, so this handler can be a no-op.
  keepalive: () => { /* no-op — silence timer reset happens in the SSE loop */ },

  // Artifact events — backend emits these for any content-producing tool.
  // Payload carries full `content` + `previous_content` (identifiers
  // stripped server-side via `to_sse_dict`), so `chat/artifactMarkers`
  // can expand `[ARTIFACT: art_xxx]` inline on first paint without a
  // REST round-trip. The `code_change` SSE event still drives the
  // Monaco/Compare panel.
  artifact_complete: (d: any) => {
    const art = d?.artifact;
    if (art && typeof art.id === "string" && art.id.startsWith("art_")) {
      console.debug("[SSE artifact_complete]", {
        id: art.id,
        name: art.name,
        hasContent: typeof art.content === "string" && art.content.length > 0,
        contentLength: typeof art.content === "string" ? art.content.length : 0,
        sessionUuid: S().sessionUuid,
      });
      S().setArtifact(art);
      const stored = S().artifactsById[art.id];
      console.debug("[SSE artifact_complete stored]", {
        id: art.id,
        stored: !!stored,
        hasContent: typeof stored?.content === "string" && stored.content.length > 0,
        contentLength: typeof stored?.content === "string" ? stored.content.length : 0,
      });
    }
  },

  // Backend emits this when a Salesforce token refresh fails mid-run.
  // Route through the same errorHandler path the 401 axios interceptor uses,
  // so the re-auth modal opens instead of just a chat bubble.
  auth_required: (d: any) => {
    useDevAgentStore.getState().setSfConnected(false);
    import("../errorHandler").then(({ handleError }) => {
      handleError(
        { response: { status: 401, data: { code: "reauth_required" } }, payload: d },
        "session",
      );
    });
  },
  artifact_start: (d: any) => {
    const art = d?.artifact || {};
    const name = art?.name || "artifact";
    upsertThinking(`Generating ${name}...`, `artifact:${art?.id || name}`);
  },
  codegen_status: (d: any) => {
    const kind = d?.kind === "test" ? "test class" : "refactor";
    const target = d?.target || "code";
    const stage = d?.stage || "starting";
    if (stage === "starting") upsertThinking(`Generating ${kind} for ${target}...`, `codegen:${kind}:${target}`);
  },
};

const SILENCE_TIMEOUT_MS = 10_000;

/**
 * Recover content from DB when SSE streaming is incomplete or missing.
 * Single function called from both run_end and silence timer — DB is
 * the single source of truth, SSE is just the live typing effect.
 */
/** Merge DB assistant rows into the live feed. Pass `msgs` to avoid a second /history round-trip. */
function applyHistoryToFeed(uuid: string, msgs: any[]): void {
  if (S().sessionUuid !== uuid) return;
  const items = S().feedItems;
  const toolCallsFor = (msg: any): unknown[] => {
    const meta = msg?.metadata || msg?.metadata_ || {};
    return Array.isArray(meta?.tool_calls) ? meta.tool_calls : [];
  };

  const feedUuids = new Set(
    items.filter((f) => f.data?.messageUuid).map((f) => f.data.messageUuid),
  );

  for (const item of items) {
    if (item.type !== "chat_agent" || !item.data?.messageUuid) continue;
    const dbMsg = msgs.find((m: any) => m.uuid === item.data.messageUuid);
    if (!dbMsg) continue;
    const patch: Record<string, unknown> = {};
    if (dbMsg.content && dbMsg.content !== item.data.content) {
      patch.content = dbMsg.content;
    }
    const dbToolCalls = toolCallsFor(dbMsg);
    if (dbToolCalls.length > 0 && (!Array.isArray(item.data?.toolCalls) || item.data.toolCalls.length === 0)) {
      patch.toolCalls = dbToolCalls;
    }
    if (Object.keys(patch).length > 0) {
      S().updateFeedItem(item.id, patch);
    }
  }

  const latestDbAssistant = [...msgs].reverse().find(
    (m: any) => m.role === "assistant" && m.content && !feedUuids.has(m.uuid),
  );
  if (!latestDbAssistant) return;

  const lastFeedAgent = [...items].reverse().find((f) => f.type === "chat_agent");
  const dbMeta = latestDbAssistant?.metadata || latestDbAssistant?.metadata_ || undefined;
  if (lastFeedAgent && !lastFeedAgent.data?.messageUuid) {
    S().updateFeedItem(lastFeedAgent.id, {
      content: latestDbAssistant.content,
      messageUuid: latestDbAssistant.uuid,
      toolCalls: toolCallsFor(latestDbAssistant),
      // Carry the persisted message metadata (blocked_by, blocked_action, ...)
      // so BlockedActionBubble can render the trigger-ingest button.
      metadata: dbMeta,
    });
  } else if (!lastFeedAgent || lastFeedAgent.data?.messageUuid !== latestDbAssistant.uuid) {
    S().addFeedItem({
      type: "chat_agent",
      data: {
        content: latestDbAssistant.content,
        messageUuid: latestDbAssistant.uuid,
        toolCalls: toolCallsFor(latestDbAssistant),
        metadata: dbMeta,
      },
    });
  }
}

function recoverFromDB(preloadedMsgs?: any[]): void {
  const uuid = S().sessionUuid;
  if (!uuid) return;

  if (preloadedMsgs) {
    applyHistoryToFeed(uuid, preloadedMsgs);
    return;
  }

  import("../devAgentService").then(({ getDevAgentSessionHistory }) => {
    getDevAgentSessionHistory(uuid).then((msgs) => {
      applyHistoryToFeed(uuid, msgs);
    }).catch(() => {});
  });
}

/**
 * Frontend silence gate. After SILENCE_TIMEOUT_MS of no SSE events,
 * finalize streaming + fetch from DB. 10s = 2 missed keepalives.
 */
let _silenceRetries = 0;
const MAX_SILENCE_RETRIES = 3;

export function armSilenceTimer(): void {
  if (sseState.stuckTimer) clearTimeout(sseState.stuckTimer);
  _silenceRetries = 0;
  sseState.stuckTimer = setTimeout(_onSilence, SILENCE_TIMEOUT_MS);
}

function _onSilence(): void {
  const status = S().runStatus;
  if (status !== "running" && status !== "idle") return;
  // Idle + no partial stream: nothing to "un-stick" — avoid polling /history
  // on a timer (was firing after session switches and duplicating hydrate).
  if (status === "idle" && !S().streamingContent) return;

  const lastItem = S().feedItems[S().feedItems.length - 1];
  const waitingForUser = lastItem?.type === "clarification"
    || lastItem?.type === "approval"
    || lastItem?.type === "plan"
    || S().pendingApproval != null;
  if (waitingForUser) return;

  const uuid = S().sessionUuid;
  if (!uuid) { _finalizeSilence(); return; }

  import("../devAgentService").then(({ getDevAgentSessionHistory }) => {
    getDevAgentSessionHistory(uuid).then((msgs) => {
      if (S().sessionUuid !== uuid) return;
      const lastDbAssistant = [...msgs].reverse().find(
        (m: any) => m.role === "assistant" && m.content,
      );

      if (lastDbAssistant?.content) {
        const lastFeedAgent = [...S().feedItems].reverse().find(
          (f) => f.type === "chat_agent" && f.data?.content,
        );
        const isNew = !lastFeedAgent || lastDbAssistant.content !== lastFeedAgent.data.content;
        if (isNew) {
          _finalizeSilence();
          recoverFromDB(msgs);
          return;
        }
      }

      _silenceRetries++;
      if (_silenceRetries >= MAX_SILENCE_RETRIES) {
        _finalizeSilence();
        recoverFromDB();
        setTimeout(() => {
          if (S().sessionUuid !== uuid) return;
          const hasContent = S().feedItems.some(
            (f) => f.type === "chat_agent" && f.data?.content && f.timestamp > (Date.now() - 60000),
          );
          if (!hasContent && S().runStatus === "running") {
            import("react-hot-toast").then(({ default: toast }) => {
              toast.error("Response may have been lost — try refreshing the page");
            });
          }
        }, 2000);
      } else {
        sseState.stuckTimer = setTimeout(_onSilence, SILENCE_TIMEOUT_MS);
      }
    }).catch(() => {
      _finalizeSilence();
    });
  });
}

function _finalizeSilence(): void {
  const sc = S().streamingContent;
  if (sc) {
    sseState.finalized = true;
    S().finalizeStreaming(sc);
  }
  S().setRunStatus("idle");
  clearThinking();
  sseState.toolCallMap.forEach((feedId) => S().updateFeedItem(feedId, { status: "done" }));
  sseState.toolCallMap.clear();
  _silenceRetries = 0;
}